import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, Car, Plane } from "lucide-react";
import { MaskLines } from "./Reveal";
import { HERO_SLIDES, scrollToId, wa } from "../lib/site";
import { HeroFrame } from "./HeroFrame";

export const Hero = () => {
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 18 });
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 18 });

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="home" ref={ref} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}
      className="relative overflow-hidden bg-paper pt-28 sm:pt-32 pb-16 lg:pb-24" data-testid="hero-section">
      <div className="pointer-events-none absolute -right-48 top-[620px] h-[420px] w-[420px] lg:-right-40 lg:-top-40 lg:h-[560px] lg:w-[560px] rounded-full bg-go" />
      <div className="pointer-events-none absolute inset-0 grain" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-12 lg:gap-8">
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-ink">
            <span className="h-2 w-2 rounded-full bg-go animate-pulse" /> Domestic · International · Fleet
          </motion.div>
          <h1 className="font-display font-extrabold text-ink text-[13vw] leading-[0.88] sm:text-7xl lg:text-[6.6rem] tracking-[-0.04em]" data-testid="hero-headline">
            <MaskLines onMount delay={0.35} lines={[
              "Explore the",
              <span key="w">World <span className="font-serif-i font-normal italic text-go tracking-normal">with</span></span>,
              "Go Trip Travels",
            ]} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-7 max-w-md text-base md:text-lg text-zinc-700 leading-relaxed" data-testid="hero-subheadline">
            Your trusted partner for unforgettable domestic getaways and international adventures.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-9 flex flex-col sm:flex-row flex-wrap gap-3">
            <button onClick={() => scrollToId("domestic")} data-testid="hero-explore-domestic-btn"
              className="group inline-flex items-center justify-between gap-6 rounded-full bg-ink pl-6 pr-2 py-2 text-white font-bold transition-[background-color] duration-300 hover:bg-go hover:text-ink">
              Explore Domestic Packages
              <span className="grid h-10 w-10 place-items-center rounded-full bg-go text-ink transition-transform duration-500 group-hover:rotate-[-45deg] group-hover:bg-ink group-hover:text-white">
                <ArrowDownRight size={18} />
              </span>
            </button>
            <button onClick={() => scrollToId("fleet")} data-testid="hero-rent-vehicle-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-ink px-6 py-3.5 font-bold text-ink transition-[background-color,color] duration-300 hover:bg-ink hover:text-white">
              <Car size={18} /> Rent a Vehicle
            </button>
            <a href={wa("Hi, I want to book a flight ticket. Please share options and fares.")} target="_blank" rel="noopener noreferrer"
              data-testid="hero-book-flights-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-go px-6 py-3.5 font-bold text-go transition-[background-color,color] duration-300 hover:bg-go hover:text-ink">
              <Plane size={18} /> Book Flights
            </a>
          </motion.div>
        </motion.div>
        <div className="lg:col-span-5 relative" style={{ perspective: 1200 }}>
          <HeroFrame idx={idx} setIdx={setIdx} rotX={rotX} rotY={rotY} imgY={imgY} />
        </div>
      </div>
    </section>
  );
};
